import { useState } from 'react';
import { DataTable } from '@/components/tables';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, MoreHorizontal, Eye, Download, Send, Trash2 } from 'lucide-react';
import { formatCurrency, formatDate, cn, getStatusColor } from '@/lib/utils';
import { mockInvoices } from '@/lib/mock-data';

const columns = [
  {
    accessorKey: 'id',
    header: 'Invoice #',
    cell: ({ row }) => <span className="font-mono font-medium">{row.original.id}</span>,
  },
  {
    accessorKey: 'customer',
    header: 'Customer',
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    cell: ({ row }) => <span className="font-semibold">{formatCurrency(row.original.amount)}</span>,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge className={cn('capitalize', getStatusColor(row.original.status))}>
        {row.original.status}
      </Badge>
    ),
  },
  {
    accessorKey: 'date',
    header: 'Issue Date',
    cell: ({ row }) => formatDate(row.original.date),
  },
  {
    accessorKey: 'dueDate',
    header: 'Due Date',
    cell: ({ row }) => formatDate(row.original.dueDate),
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>
            <Eye className="h-4 w-4 mr-2" />
            View Invoice
          </DropdownMenuItem>
          <DropdownMenuItem>
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </DropdownMenuItem>
          <DropdownMenuItem>
            <Send className="h-4 w-4 mr-2" />
            Send to Customer
          </DropdownMenuItem>
          <DropdownMenuItem className="text-destructive">
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];

export default function InvoicesPage() {
  const [invoices] = useState(mockInvoices);
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredInvoices = statusFilter === 'all'
    ? invoices
    : invoices.filter((i) => i.status === statusFilter);

  const totalAmount = invoices.reduce((sum, i) => sum + i.amount, 0);
  const paidAmount = invoices
    .filter((i) => i.status === 'paid')
    .reduce((sum, i) => sum + i.amount, 0);
  const pendingAmount = invoices
    .filter((i) => i.status === 'pending' || i.status === 'overdue')
    .reduce((sum, i) => sum + i.amount, 0);

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Invoices</h1>
          <p className="text-muted-foreground">Manage and track your invoices</p>
        </div>
        <Button data-testid="create-invoice-button" className="w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          Create Invoice
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl border bg-card">
          <p className="text-sm text-muted-foreground">Total Invoiced</p>
          <p className="text-2xl font-bold">{formatCurrency(totalAmount)}</p>
        </div>
        <div className="p-4 rounded-xl border bg-green-50 dark:bg-green-900/20">
          <p className="text-sm text-green-600">Paid</p>
          <p className="text-2xl font-bold text-green-700">{formatCurrency(paidAmount)}</p>
        </div>
        <div className="p-4 rounded-xl border bg-yellow-50 dark:bg-yellow-900/20">
          <p className="text-sm text-yellow-600">Outstanding</p>
          <p className="text-2xl font-bold text-yellow-700">{formatCurrency(pendingAmount)}</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <div className="overflow-x-auto">
          <TabsList className="w-max min-w-full sm:min-w-0">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="paid">Paid</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="overdue">Overdue</TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      <DataTable
        columns={columns}
        data={filteredInvoices}
        searchPlaceholder="Search invoices..."
      />
    </div>
  );
}
